// src/components/MemorialCard.tsx
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, X, Calendar, Award, Quote } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface MemorialCardProps {
  name: string;
  role: string;
  years: string;
  image?: string;
  bio?: string;
  achievements?: string[];
  quote?: string;
  dateOfPassing?: string;
  delay?: number;
}

export const MemorialCard = ({ 
  name, 
  role, 
  years, 
  image, 
  bio, 
  achievements,
  quote,
  dateOfPassing,
  delay = 0 
}: MemorialCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay }}
        whileHover={{ y: -8 }}
        onClick={() => setIsExpanded(true)}
        className="group bg-card border-2 border-border hover:border-primary/30 rounded-3xl overflow-hidden cursor-pointer shadow-xl hover:shadow-2xl transition-all duration-500"
      >
        {/* Image Section */}
        <div className="relative aspect-[3/4] bg-muted overflow-hidden">
          {image ? (
            <>
              <img
                src={image}
                alt={name}
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                loading="lazy"
              />
              {/* Overlay Gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent opacity-90" />
              
              {/* Memorial Badge */}
              <div className="absolute top-4 right-4 w-12 h-12 rounded-full bg-background/90 backdrop-blur-sm border-2 border-primary/40 flex items-center justify-center shadow-xl">
                <Heart className="h-6 w-6 text-primary animate-pulse" />
              </div>
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
              <Heart className="h-20 w-20 text-primary/20" />
            </div>
          )}

          {/* Years Badge */}
          {years && (
            <div className="absolute bottom-4 left-4 right-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/90 backdrop-blur-sm border border-primary/20 shadow-lg">
                <Calendar className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold text-primary">{years}</span>
              </div>
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="p-6 text-center">
          {/* Name */}
          <h3 className="text-2xl font-bold mb-2 group-hover:text-primary transition-colors">
            {name}
          </h3>

          {/* Role */}
          <div className="inline-flex items-center gap-2 px-4 py-1 rounded-full bg-primary/10 border border-primary/20 mb-3">
            <Award className="h-4 w-4 text-primary" />
            <p className="text-sm font-semibold text-primary">{role}</p>
          </div>

          {/* Bio Preview */}
          {bio && (
            <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 mb-4">
              {bio}
            </p>
          )}

          {/* Read More */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-primary hover:text-primary hover:bg-primary/10"
          >
            {/* This will be in i18n */}
            Read Full Story
          </Button>
        </div>
      </motion.div>

      {/* Expanded Modal */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsExpanded(false)}
            className="fixed inset-0 bg-background/95 backdrop-blur-md z-50 flex items-center justify-center p-4 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-card border-2 border-border rounded-3xl max-w-3xl w-full shadow-2xl overflow-hidden my-8"
            >
              {/* Close Button */}
              <button
                onClick={() => setIsExpanded(false)}
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-background/90 backdrop-blur-sm border border-border hover:border-primary/50 flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="grid md:grid-cols-5 gap-0">
                {/* Image Section */}
                <div className="md:col-span-2 relative aspect-[3/4] md:aspect-auto bg-muted">
                  {image ? (
                    <>
                      <img
                        src={image}
                        alt={name}
                        className="w-full h-full object-cover grayscale"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent" />
                      
                      {/* Memorial Icon */}
                      <div className="absolute top-6 right-6 w-16 h-16 rounded-full bg-background/90 backdrop-blur-sm border-2 border-primary/40 flex items-center justify-center shadow-xl">
                        <Heart className="h-8 w-8 text-primary" />
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
                      <Heart className="h-32 w-32 text-primary/20" />
                    </div>
                  )}
                </div>

                {/* Content Section */}
                <div className="md:col-span-3 p-8">
                  {/* Header */}
                  <div className="mb-6">
                    <h2 className="text-4xl font-bold mb-3">{name}</h2>
                    
                    <div className="flex flex-wrap gap-3 mb-4">
                      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                        <Award className="h-5 w-5 text-primary" />
                        <span className="text-sm font-semibold text-primary">{role}</span>
                      </div>

                      {years && (
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                          <Calendar className="h-5 w-5 text-primary" />
                          <span className="text-sm font-semibold text-primary">{years}</span>
                        </div>
                      )}
                    </div>

                    {dateOfPassing && (
                      <p className="text-sm text-muted-foreground">
                        {/* This will be in i18n */}
                        Passed: {dateOfPassing}
                      </p>
                    )}
                  </div>

                  {/* Divider */}
                  <div className="w-16 h-1 bg-gradient-to-r from-primary to-accent rounded-full mb-6" />

                  {/* Bio */}
                  {bio && (
                    <div className="mb-6">
                      <h3 className="text-lg font-bold mb-3">Biography</h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {bio}
                      </p>
                    </div>
                  )}

                  {/* Achievements */}
                  {achievements && achievements.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-bold mb-3">Key Achievements</h3>
                      <ul className="space-y-2">
                        {achievements.map((achievement, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Award className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-muted-foreground">{achievement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Quote */}
                  {quote && (
                    <div className="p-6 rounded-2xl bg-primary/5 border border-primary/20 relative">
                      <Quote className="h-8 w-8 text-primary/20 absolute top-4 left-4" />
                      <p className="text-foreground italic leading-relaxed pl-10">
                        "{quote}"
                      </p>
                    </div>
                  )}

                  {/* Memorial Message */}
                  <div className="mt-8 pt-6 border-t border-border text-center">
                    <Heart className="h-8 w-8 text-primary mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground italic">
                      {/* This will be in i18n */}
                      "Their legacy lives on in every child they inspired"
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
